import { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    if (!consent) {
      toast.error("Please confirm you consent to receive emails.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/newsletter", { email });
      toast.success(data.message);
      setEmail("");
      setConsent(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-onyx text-ivory relative grain border-t border-smoke/15" data-testid="site-footer">
      <div className="container-lux py-20 lg:py-28 relative z-10">
        {/* G. NEWSLETTER */}
        <div className="grid lg:grid-cols-2 gap-16 pb-16 border-b border-smoke/15">
          <div>
            <p className="label-mono mb-4 text-blood">Join the disturbed</p>
            <h3 className="heading-serif text-3xl md:text-4xl mb-8 max-w-md leading-tight">
              Whispers, launches and midnight dispatches
            </h3>
            <form onSubmit={subscribe} className="max-w-md">
              <div className="flex border-b border-smoke/40">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  data-testid="newsletter-email-input"
                  className="flex-1 bg-transparent py-3 text-sm placeholder:text-smoke focus:outline-none font-sans"
                />
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="newsletter-submit"
                  className="label-mono text-blood hover:text-ivory transition-colors disabled:opacity-50"
                >
                  {loading ? "…" : "Subscribe"}
                </button>
              </div>
              <label className="flex items-start gap-3 mt-4 cursor-pointer">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  data-testid="newsletter-consent"
                  className="mt-1 accent-[#A8303C]"
                />
                <span className="text-xs text-smoke font-sans leading-relaxed">
                  I consent to receive marketing emails from Beautifully Disturbed and understand I can
                  unsubscribe at any time.
                </span>
              </label>
            </form>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-8 lg:justify-items-end">
            <FooterCol
              title="The House"
              links={[
                { to: "/collection", label: "Collection" },
                { to: "/story", label: "Our World" },
                { to: "/journal", label: "Journal" },
                { to: "/contact", label: "Contact" },
              ]}
            />
            <FooterCol
              title="Fragrances"
              links={[
                { to: "/product/the-ghost-in-your-bed", label: "The Ghost in Your Bed" },
                { to: "/product/matryoshka-skin", label: "Matryoshka Skin" },
                { to: "/product/xoxo", label: "XOXO" },
              ]}
            />
            <FooterCol
              title="Legal & Care"
              links={[
                { to: "/legal/shipping", label: "Shipping" },
                { to: "/legal/returns", label: "Returns" },
                { to: "/legal/privacy", label: "Privacy" },
                { to: "/legal/terms", label: "Terms" },
                { to: "/legal/cookies", label: "Cookies" },
              ]}
            />
          </div>
        </div>

        <div className="pt-10 flex flex-col md:flex-row justify-between gap-5 label-mono items-start md:items-center">
          <span>© {new Date().getFullYear()} Beautifully Disturbed · The Netherlands</span>
          <div className="flex items-center gap-6">
            <a
              href="https://instagram.com"
              target="_blank"
              rel="noreferrer"
              data-testid="social-instagram"
              className="inline-flex items-center gap-2 text-ivory/70 hover:text-blood transition-colors"
            >
              <Instagram size={15} /> Instagram
            </a>
            <a
              href="https://tiktok.com"
              target="_blank"
              rel="noreferrer"
              data-testid="social-tiktok"
              className="text-ivory/70 hover:text-blood transition-colors"
            >
              TikTok
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <p className="label-mono mb-5">{title}</p>
      <ul className="space-y-3">
        {links.map((l) => (
          <li key={l.to}>
            <Link
              to={l.to}
              className="font-sans text-sm text-ivory/75 hover:text-blood transition-colors"
            >
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
